import { z } from 'zod';

const personSchema = z.object({
  name: z.string().max(120).default(''),
  title: z.string().max(160).optional(),
  email: z.string().max(200).default(''),
  phone: z.string().max(60).default(''),
});

const coachingFieldsSchema = z.object({
  templateType: z.literal('coaching_sow'),
  tierLabel: z.string().max(60),
  clientCompany: z.string().max(200),
  accountOwner: personSchema,
  csm: personSchema,
  coachee: personSchema,
  netsuiteProjectNumber: z.string().max(40),
  engagementLocation: z.string().max(120),
  contractedHours: z.number().nonnegative(),
  timeframe: z.string().max(120),
  coachName: z.string().max(120),
  coachHourlyRate: z.number().nonnegative(),
  feesTotal: z.number().nonnegative(),
  desiredOutcomes: z.string().max(8000).default(''),
  // Empty for a Coaching Extension (free-text Project Approach instead).
  phases: z
    .array(
      z.object({
        name: z.string().max(200),
        description: z.string().max(4000),
        hours: z.number().nonnegative(),
        hoursNote: z.string().max(120).optional(),
      }),
    )
    .max(20),
  projectApproach: z.string().max(8000).default(''),
  specialInstructions: z.string().max(4000).default(''),
});

const cohortFieldsSchema = z.object({
  templateType: z.literal('cohort_contract'),
  deliveryMode: z.enum(['in_person', 'virtual']),
  jobNumber: z.string().max(40),
  contractDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  effectiveDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  clientLegalName: z.string().max(240),
  groups: z
    .array(
      z.object({
        title: z.string().max(240),
        subtitle: z.string().max(240),
        lines: z
          .array(
            z.object({
              title: z.string().max(300),
              deliveryLine: z.string().max(300),
              price: z.number(),
            }),
          )
          .max(30),
      }),
    )
    .min(1)
    .max(20),
  total: z.number(),
  poRequired: z.boolean(),
  poNumber: z.string().max(80).optional(),
  billingName: z.string().max(240).default(''),
  billingEmail: z.string().max(200).default(''),
  billingAddress: z.string().max(300).default(''),
  billingCityStateZip: z.string().max(200).default(''),
  billingCountry: z.string().max(120).default(''),
});

const execCoachingFieldsSchema = z.object({
  templateType: z.literal('exec_coaching_sow'),
  scope: z.enum(['exec_coaching', 'c_suite', 'executive_partner', 'hybrid']),
  tierLabel: z.string().max(60),
  clientCompany: z.string().max(200),
  oNumber: z.string().max(40),
  contractDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  leaderName: z.string().max(160),
  coachName: z.string().max(160),
  epName: z.string().max(160),
  serviceName: z.string().max(160),
  servicesHeading: z.string().max(160),
  servicesIntro: z.string().max(4000),
  desiredOutcomes: z.string().max(8000).default(''),
  showPhaseHours: z.boolean(),
  phases: z
    .array(
      z.object({
        name: z.string().max(200),
        description: z.string().max(4000),
        hours: z.number().nonnegative(),
        hoursNote: z.string().max(120).optional(),
      }),
    )
    .max(20),
  programLines: z
    .array(z.object({ label: z.string().max(200), amountText: z.string().max(120) }))
    .min(1)
    .max(6),
  additionalFeesText: z.string().max(4000),
  budgetTimingText: z.string().max(4000),
  extensionText: z.string().max(4000),
  projectTermsText: z.string().max(8000),
  accountManagerLine: z.string().max(300).default(''),
});

const executivePartnerFieldsSchema = z.object({
  templateType: z.literal('executive_partner_sow'),
  variant: z.enum(['ep_only', 'hybrid_ep', 'hybrid_coach']),
  clientCompany: z.string().max(200),
  accountOwner: personSchema,
  csm: personSchema,
  client: personSchema,
  clientAdmin: personSchema,
  netsuiteProjectNumber: z.string().max(40),
  stakeholder1: personSchema,
  stakeholder2: personSchema,
  engagementLocation: z.string().max(120),
  ep: personSchema,
  epContractedTime: z.string().max(120),
  epTimeframe: z.string().max(120),
  coach: personSchema,
  coachContractedTime: z.string().max(120),
  coachTimeframe: z.string().max(120),
  feesText: z.string().max(120),
  desiredOutcomes: z.string().max(8000).default(''),
  projectApproach: z.string().max(8000).default(''),
  specialInstructions: z.string().max(4000).default(''),
});

export const generateSowSchema = z.object({
  opportunitySfId: z.string().regex(/^006[a-zA-Z0-9]{12,15}$/),
  fields: z.discriminatedUnion('templateType', [
    coachingFieldsSchema,
    cohortFieldsSchema,
    execCoachingFieldsSchema,
    executivePartnerFieldsSchema,
  ]),
});
